import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './CallerInfo.css';
import TabPanel from '../TabPanel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

// Helper functions
const getDisplayValue = (attribute) => {
  return attribute?.value || '-';
};

const getConsentDisplay = (eConsent) => {
  if (!eConsent) return '-';
  return eConsent.value === 'Agree' ? 'Yes' : 'No';
};

const getAuthDisplay = (authStatus) => {
  if (!authStatus) return '-';
  return authStatus.value === 'true' ? 'Yes' : 'No';
};

const getLanguageDisplay = (language) => {
  if (!language) return '-';
  return language.value === 'english' ? 'English' : language.value;
};

// Reusable components
const CopyableField = ({ label, value, onCopy }) => (
  <div style={{ float: 'left' }}>
    <div className="label fs-unmask">
      {label}
      {value && (
        <ContentCopyIcon
          sx={{
            position: 'relative',
            left: '5px',
            ":hover": {
              bgcolor: "white",
              color: "#1976d2",
              scale: "1.3"
            }
          }}
          className="copyIcon"
          style={{ width: '15px', height: '15px' }}
          onClick={() => onCopy(value)}
        />
      )}
    </div>
    <div className="data fs-unmask">{value || '-'}</div>
  </div>
);

const SimpleField = ({ label, value }) => (
  <div style={{ float: 'left' }}>
    <div className="label fs-unmask">{label}</div>
    <div className="data fs-unmask">{value || '-'}</div>
  </div>
);

CopyableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onCopy: PropTypes.func.isRequired,
};

SimpleField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const Item = styled(Paper)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

const MyTab = styled(Tab)({
  fontFamily: 'Open Sans',
  fontWeight: '600',
  color: '#525252',
});

const CallerInfo = (props) => {
  const [contactAttributes, setContactAttributes] = useState({});
  const [value, setValue] = useState(0);
  const [open, setOpen] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleToClose = (event, reason) => {
    if ('clickaway' === reason) return;
    setOpen(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setOpen(true);
  };

  useEffect(() => {
    if (props.contactAttributes) {
      setContactAttributes(props.contactAttributes);
    }
  }, [props]);

  return (
    <div
      id="callerInfoTopLevel"
      style={{ marginLeft: '20px', marginRight: '20px', paddingBottom: '50px' }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <MyTab label="Personal" className={'test fs-unmask'} {...a11yProps(0)} />
          <MyTab label="Employer" className={'test fs-unmask'} {...a11yProps(1)} />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <div id="personalInfo">
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    DOB
                  </div>
                  <div id="data" className="data">
                    {getDisplayValue(contactAttributes.plainTextDob)}
                  </div>
                </div>
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    E-Consent Received
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getConsentDisplay(contactAttributes.eConsent)}
                  </div>
                </div>
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    Customer No
                    {contactAttributes.customerNumber &&
                      contactAttributes.customerNumber.value !== '' ? (
                        <ContentCopyIcon
                          sx={{
                            position: 'relative',
                            left: '5px',
                            ":hover": {
                              bgcolor: "white",
                              color: "#1976d2",
                              scale: "1.3"
                            }
                          }}
                          className="copyIcon"
                          style={{ width: '15px', height: '15px' }}
                          onClick={() => {
                            handleCopy(contactAttributes.customerNumber.value);
                          }}
                        ></ContentCopyIcon>
                      ) : (
                        ''
                      )}
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getDisplayValue(contactAttributes.customerNumber)}
                  </div>
                </div>
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    Authenticated
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getAuthDisplay(contactAttributes.fineos_auth_status)}
                  </div>
                </div>
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    Web ID
                    {contactAttributes.eid &&
                      contactAttributes.eid.value !== '' ? (
                        <ContentCopyIcon
                          sx={{
                            position: 'relative',
                            left: '5px',
                            ":hover": {
                              bgcolor: "white",
                              color: "#1976d2",
                              scale: "1.3"
                            }
                          }}
                          className="copyIcon"
                          style={{ width: '15px', height: '15px' }}
                          onClick={() => {
                            handleCopy(contactAttributes.eid.value);
                          }}
                        ></ContentCopyIcon>
                      ) : (
                        ''
                      )}
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getDisplayValue(contactAttributes.eid)}
                  </div>
                </div>
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    Language
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getLanguageDisplay(contactAttributes.Language)}
                  </div>
                </div>
              </Item>
            </Grid>
          </Grid>
        </div>
      </TabPanel>
      <TabPanel value={value} index={1}>
        <div id="employerInfo">
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          >
            <Grid item xs={12}>
              <Item>
                <div style={{ float: 'left' }}>
                  <div id="label" className="label fs-unmask">
                    Company
                    {contactAttributes.employerName &&
                      contactAttributes.employerName.value !== '' ? (
                        <ContentCopyIcon
                          sx={{
                            position: 'relative',
                            left: '5px',
                            ":hover": {
                              bgcolor: "white",
                              color: "#1976d2",
                              scale: "1.3"
                            }
                          }}
                          className="copyIcon"
                          style={{ width: '15px', height: '15px' }}
                          onClick={() => {
                            handleCopy(contactAttributes.employerName.value);
                          }}
                        ></ContentCopyIcon>
                      ) : (
                        ''
                      )}
                  </div>
                  <div id="data" className="data fs-unmask">
                    {getDisplayValue(contactAttributes.employerName)}
                  </div>
                </div>
              </Item>
            </Grid>
          </Grid>
        </div>
      </TabPanel>
      {/* Single Snackbar for all copy operations */}
      <Snackbar
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={open}
        autoHideDuration={5000}
        message="Copied."
        onClose={handleToClose}
        action={
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={handleToClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </div>
  );
};

CallerInfo.propTypes = {
  contactAttributes: PropTypes.object,
};

export default CallerInfo;
